import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../api/endpoints';
import { Modal, Empty, Button } from '../../components/Ui';
import MapPicker, { addressFor } from '../../components/MapPicker';
import { Reveal, stagger } from '../../components/Motion';
import { requestLocationPermission, getCurrentPosition } from '../../services/location';
import { useApp } from '../../context/AppContext';
import { colors } from '../../theme';

/**
 * Choose a pickup or drop point.
 *
 * Search runs through our own API rather than calling Google from the browser,
 * so the Maps key never leaves the server. Falls back to the browser's own
 * position when search is unavailable.
 */
const PickLocationModal = ({ open, title = 'Choose location', onClose, onPicked }) => {
  const { location } = useApp();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  // Map mode: drop the pin on the exact spot instead of trusting an address.
  const [onMap, setOnMap] = useState(false);
  const [pin, setPin] = useState(null);
  const [pinAddress, setPinAddress] = useState('');
  const [resolving, setResolving] = useState(false);
  const [openingMap, setOpeningMap] = useState(false);
  // Once the user moves the pin, a late GPS fix must not yank it away.
  const pinTouched = useRef(false);

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); setUnavailable(false); setOnMap(false); }
  }, [open]);

  useEffect(() => {
    if (!open || query.trim().length < 3) { setResults([]); return undefined; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.maps.places({
          q: query.trim(), lat: location.latitude, lng: location.longitude,
        });
        setResults(res.data || []);
        setUnavailable((res.data || []).length === 0 && query.trim().length > 4);
      } catch (err) {
        setResults([]);
        setUnavailable(true);
      } finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [query, location, open]);

  const choose = useCallback(async (item) => {
    setSearching(true);
    try {
      const res = await api.maps.placeDetails(item.placeId);
      if (res.data && res.data.coordinates) {
        onPicked({ coordinates: res.data.coordinates, address: res.data.address || item.title });
        onClose();
      }
    } catch (err) {
      onPicked({ coordinates: [location.longitude, location.latitude], address: item.title });
      onClose();
    } finally { setSearching(false); }
  }, [onPicked, onClose, location]);

  const movePin = useCallback((coordinates, byUser = false) => {
    if (byUser) pinTouched.current = true;
    setPin(coordinates);
    setResolving(true);
    addressFor(coordinates)
      .then((a) => setPinAddress(a || 'Dropped pin'))
      .finally(() => setResolving(false));
  }, []);

  /**
   * Opens the map straight away on the last known position rather than waiting
   * for a browser geolocation prompt - that wait made the button look dead. The
   * precise fix is applied after, unless the pin has already been moved.
   */
  const openMap = async () => {
    if (openingMap) return;
    setOpeningMap(true);
    const start = pin || [location.longitude, location.latitude];
    pinTouched.current = false;
    setOnMap(true);
    movePin(start);
    try {
      const ok = await requestLocationPermission();
      const fix = ok ? await getCurrentPosition() : null;
      if (fix && fix.coordinates && !pinTouched.current) movePin(fix.coordinates);
    } catch (err) {
      // Already open on the fallback position; nothing to undo.
    } finally { setOpeningMap(false); }
  };

  /** Recentre the map on a searched place without leaving the map. */
  const jumpTo = useCallback(async (item) => {
    try {
      const res = await api.maps.placeDetails(item.placeId);
      if (res.data && res.data.coordinates) {
        pinTouched.current = true;
        setPin(res.data.coordinates);
        setPinAddress(res.data.address || item.title);
      }
    } catch (err) { /* leave the pin where it is */ }
  }, []);

  const confirmPin = () => {
    if (!pin) return;
    onPicked({ coordinates: pin, address: pinAddress || 'Dropped pin' });
    onClose();
  };

  const useCurrent = async () => {
    setLocating(true);
    try {
      const ok = await requestLocationPermission();
      const fix = ok ? await getCurrentPosition() : null;
      const coordinates = (fix && fix.coordinates) || [location.longitude, location.latitude];

      let address = 'Current location';
      try {
        const rev = await api.maps.reverse({ lat: coordinates[1], lng: coordinates[0] });
        if (rev.data && rev.data.address) address = rev.data.address;
      } catch (e) { /* keep the generic label */ }

      onPicked({ coordinates, address });
      onClose();
    } finally { setLocating(false); }
  };

  if (onMap) {
    return (
      <Modal open={open} onClose={onClose} title={`${title} · move the pin`}>
        {/* Search sits above the map so an area can be found, then fine-tuned. */}
        <div className="input-wrap" style={{ background: colors.bg, border: 0 }}>
          <span>🔍</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search an area, then drag the pin"
            aria-label="Search for an area"
          />
        </div>

        {results.length && query.trim().length >= 3 ? (
          <div style={{
            marginTop: 6, border: `1px solid ${colors.line}`, borderRadius: 12, overflow: 'hidden',
          }}>
            {results.slice(0, 4).map((item) => (
              <button
                key={item.placeId}
                type="button"
                onClick={() => { jumpTo(item); setQuery(''); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                  padding: '10px 12px', background: '#fff', border: 0,
                  borderBottom: `1px solid ${colors.line}`, cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 14 }}>📍</span>
                <span className="grow">
                  <span className="t-title clamp-1" style={{ display: 'block' }}>{item.title}</span>
                  {item.subtitle ? <span className="t-tiny clamp-1">{item.subtitle}</span> : null}
                </span>
              </button>
            ))}
          </div>
        ) : null}

        <div style={{ marginTop: 12 }}>
          <MapPicker value={pin} onChange={(c) => movePin(c, true)} height={320} />
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, padding: 12,
          border: `1px solid ${colors.line}`, borderRadius: 14, background: '#fff',
        }}>
          <span style={{ fontSize: 17 }}>📍</span>
          <span className="grow">
            <span className="t-tiny" style={{ display: 'block' }}>SELECTED LOCATION</span>
            <span className="t-title" style={{ display: 'block' }}>
              {resolving ? 'Finding address…' : (pinAddress || 'Dropped pin')}
            </span>
            {pin ? (
              <span className="t-tiny">{pin[1].toFixed(5)}, {pin[0].toFixed(5)}</span>
            ) : null}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
          <Button variant="light" onClick={() => setOnMap(false)} style={{ flex: 1 }}>
            Search instead
          </Button>
          <Button onClick={confirmPin} disabled={!pin} style={{ flex: 1.3 }}>
            Confirm location
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="input-wrap" style={{ background: colors.bg, border: 0 }}>
        <span>🔍</span>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search an address, area or landmark"
          aria-label="Search for a location"
        />
      </div>

      <button
        type="button"
        onClick={useCurrent}
        disabled={locating}
        className="press"
        style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
          background: colors.primaryLight, border: 0, borderRadius: 14, padding: 14, marginTop: 12,
        }}
      >
        <span style={{ fontSize: 18 }}>{locating ? '⏳' : '🎯'}</span>
        <span className="grow">
          <span className="t-title" style={{ display: 'block', color: colors.primary }}>
            {locating ? 'Getting your location…' : 'Use my current location'}
          </span>
          <span className="t-tiny">Fastest option if you are at the spot</span>
        </span>
      </button>

      <button
        type="button"
        onClick={openMap}
        disabled={openingMap}
        className="press"
        style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
          background: '#fff', border: `1px solid ${colors.line}`, borderRadius: 14,
          padding: 14, marginTop: 10, cursor: 'pointer',
        }}
      >
        <span style={{ fontSize: 18 }}>🗺️</span>
        <span className="grow">
          <span className="t-title" style={{ display: 'block' }}>
            {openingMap ? 'Opening map…' : 'Choose on map'}
          </span>
          <span className="t-tiny">Drop a pin on the exact gate, lane or building</span>
        </span>
        <span style={{ color: colors.faint, fontSize: 18 }}>›</span>
      </button>

      <div style={{ marginTop: 14, maxHeight: '46vh', overflowY: 'auto' }}>
        {searching ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <span className="spinner" style={{ color: colors.primary }} />
          </div>
        ) : results.length ? results.map((item, i) => (
          <Reveal key={item.placeId} delay={stagger(i, 30, 200)}>
            <button
              type="button"
              onClick={() => choose(item)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                padding: '12px 4px', background: 'none', border: 0,
                borderBottom: `1px solid ${colors.line}`,
              }}
            >
              <span style={{ fontSize: 16 }}>📍</span>
              <span className="grow">
                <span className="t-title clamp-1" style={{ display: 'block' }}>{item.title}</span>
                {item.subtitle ? <span className="t-tiny clamp-1">{item.subtitle}</span> : null}
              </span>
            </button>
          </Reveal>
        )) : query.length >= 3 ? (
          <Empty
            emoji="🔍"
            title={unavailable ? 'Search is unavailable' : 'No matches'}
            text={unavailable
              ? 'Address search needs the Google Maps key on the server. You can still use your current location.'
              : 'Try a nearby landmark or a wider area name.'}
            style={{ minHeight: 0, padding: 20 }}
          />
        ) : null}
      </div>
    </Modal>
  );
};

export default PickLocationModal;
